import { createTheme, type MantineColorsTuple, MantineProvider } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { Outlet } from '@tanstack/react-router'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/id'
import { useEffect, useMemo } from 'react'
import { DevInspector } from '../DevInspector'
import { useMaintenanceStatus, useSession } from '../hooks/useApi'
import { useAppConfig } from '../hooks/useAppConfig'
import { isDocumentTitleClaimed } from '../lib/document-title'
import { MaintenancePage } from './MaintenancePage'

// Aktifkan parsing format kustom (dipakai util date-format untuk baca data lama)
// dan set locale Indonesia untuk seluruh komponen @mantine/dates.
dayjs.extend(customParseFormat)
dayjs.locale('id')

// Mantine default dark is cold blue-gray. This replaces it
// with warm purple-tinted darks for a modern feel.
const WARM_DARK: MantineColorsTuple = [
  '#C9C5CA', // 0 — lightest text
  '#A09DA1', // 1
  '#787578', // 2
  '#605D61', // 3
  '#48454A', // 4 — borders
  '#353237', // 5 — elevated surfaces
  '#2A2730', // 6 — cards / paper
  '#211E28', // 7 — sidebar / navbar
  '#1A1720', // 8 — main background
  '#141118', // 9 — deepest
]

export function RootLayout() {
  const { data: config } = useAppConfig()
  const { data: maintenance } = useMaintenanceStatus()
  const { data: session } = useSession()

  const theme = useMemo(
    () =>
      createTheme({
        primaryColor: config?.primaryColor ?? 'blue',

        colors: {
          dark: WARM_DARK,
        },

        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        fontFamilyMonospace: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
        headings: {
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          fontWeight: '700',
        },
        fontSizes: {
          xs: '0.75rem',
          sm: '0.8125rem',
          md: '0.875rem',
          lg: '1rem',
          xl: '1.125rem',
        },

        spacing: {
          xs: '0.5rem',
          sm: '0.75rem',
          md: '1rem',
          lg: '1.25rem',
          xl: '1.75rem',
        },
        radius: {
          xs: '4px',
          sm: '6px',
          md: '8px',
          lg: '12px',
          xl: '16px',
        },
        defaultRadius: 'md',

        shadows: {
          xs: '0 1px 2px rgba(0,0,0,0.12)',
          sm: '0 1px 3px rgba(0,0,0,0.16), 0 1px 2px rgba(0,0,0,0.12)',
          md: '0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.14)',
          lg: '0 10px 20px rgba(0,0,0,0.25), 0 3px 6px rgba(0,0,0,0.16)',
          xl: '0 15px 40px rgba(0,0,0,0.3), 0 5px 15px rgba(0,0,0,0.18)',
        },

        components: {
          Paper: {
            defaultProps: { shadow: 'sm', radius: 'lg' },
            styles: () => ({
              root: {
                border: '1px solid var(--mantine-color-default-border)',
                transition: 'box-shadow 200ms ease, transform 200ms ease',
              },
            }),
          },
          Card: {
            defaultProps: { shadow: 'sm', radius: 'lg' },
            styles: () => ({
              root: {
                border: '1px solid var(--mantine-color-default-border)',
                transition: 'box-shadow 200ms ease, transform 200ms ease',
              },
            }),
          },
          Button: {
            defaultProps: { radius: 'md' },
            styles: () => ({
              root: {
                transition: 'background-color 150ms ease, transform 100ms ease, box-shadow 150ms ease',
                fontWeight: 600,
              },
            }),
          },
          ActionIcon: {
            defaultProps: { radius: 'md' },
            styles: () => ({
              root: {
                transition: 'background-color 150ms ease, transform 100ms ease',
              },
            }),
          },
          Badge: {
            defaultProps: { radius: 'xl' },
            styles: () => ({
              root: {
                fontWeight: 600,
                textTransform: 'none' as const,
              },
            }),
          },
          NavLink: {
            defaultProps: { radius: 'md' },
            styles: () => ({
              root: {
                borderRadius: 'var(--mantine-radius-md)',
                transition: 'background-color 150ms ease, color 150ms ease',
                marginBottom: 2,
                fontWeight: 500,
              },
            }),
          },
          Table: {
            styles: () => ({
              table: { borderCollapse: 'separate' as const, borderSpacing: 0 },
              tr: { transition: 'background-color 150ms ease' },
              th: {
                fontWeight: 600,
                fontSize: 'var(--mantine-font-size-sm)',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.05em',
                color: 'var(--mantine-color-dimmed)',
                borderBottom: '1px solid var(--mantine-color-default-border)',
                padding: '14px 16px',
              },
              td: {
                borderBottom: '1px solid var(--mantine-color-default-border)',
                padding: '12px 16px',
              },
            }),
          },
          Modal: {
            defaultProps: {
              radius: 'lg',
              overlayProps: { backgroundOpacity: 0.6, blur: 8 },
            },
            styles: () => ({
              content: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          TextInput: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
              },
            }),
          },
          Textarea: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
              },
            }),
          },
          Select: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          NumberInput: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          PasswordInput: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          FileInput: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          MultiSelect: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              input: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          Menu: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              dropdown: {
                border: '1px solid var(--mantine-color-default-border)',
                backdropFilter: 'blur(12px)',
              },
            }),
          },
          Tooltip: {
            defaultProps: { radius: 'md' },
          },
          Notification: {
            defaultProps: { radius: 'lg' },
            styles: () => ({
              root: {
                border: '1px solid var(--mantine-color-default-border)',
              },
            }),
          },
          Accordion: {
            styles: () => ({
              item: {
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-lg)',
                transition: 'background-color 150ms ease',
              },
              control: {
                borderRadius: 'var(--mantine-radius-lg)',
              },
            }),
          },
          Tabs: {
            styles: () => ({
              tab: {
                transition: 'background-color 150ms ease, color 150ms ease',
                fontWeight: 500,
              },
            }),
          },
          SegmentedControl: {
            styles: () => ({
              root: {},
            }),
          },
          Divider: {
            styles: () => ({
              root: {
                borderColor: 'var(--mantine-color-default-border)',
              },
            }),
          },
          Stepper: {
            styles: () => ({
              stepIcon: {
                transition: 'background-color 200ms ease, border-color 200ms ease',
              },
            }),
          },
          Switch: {
            styles: () => ({
              track: {
                transition: 'background-color 200ms ease',
              },
            }),
          },
        },
      }),
    [config?.primaryColor],
  )

  useEffect(() => {
    // Jangan timpa judul yang sudah diklaim halaman (mis. /privasi) — lihat lib/document-title.
    if (config && !isDocumentTitleClaimed()) {
      const name = [config.appName, config.appNameSecondary].filter(Boolean).join(' ')
      document.title = `${name} — ${config.appDescription}`
    }
  }, [config])

  const userRole = (session?.user as any)?.role
  const userIsQc = (session?.user as any)?.is_qc
  const isAdmin = userRole === 'platform_admin' || userIsQc
  const showMaintenance = maintenance?.active && !isAdmin

  const isDev = import.meta.env?.DEV
  const inspectorEnabled = userRole === 'platform_admin' || userIsQc
  const inspectorDisabled = !isDev && !inspectorEnabled

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <DatesProvider settings={{ locale: 'id' }}>
        <ModalsProvider>
          <Notifications position="top-right" />
          <DevInspector copyOnly={!isDev} disabled={inspectorDisabled}>
            {showMaintenance ? <MaintenancePage message={maintenance.message} until={maintenance.until} /> : <Outlet />}
          </DevInspector>
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  )
}
